/**
 * Date and time formatting utilities
 */

import { ChartDateTime } from '../../models/storage';

/**
 * Formats a date string (YYYY-MM-DD) to a user-friendly format
 */
export function formatDate(dateStr: string, format: 'short' | 'medium' | 'long' = 'medium'): string {
  try {
    const date = new Date(dateStr);
    
    switch (format) {
      case 'short':
        return date.toLocaleDateString('en-US', {
          month: 'numeric',
          day: 'numeric',
          year: '2-digit'
        });
      
      case 'long':
        return date.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      
      case 'medium':
      default:
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
    }
  } catch (error) {
    return dateStr;
  }
}

/**
 * Formats a time string (HH:mm:ss) to a user-friendly format
 */
export function formatTime(timeStr: string, includeSeconds: boolean = false): string {
  try {
    const [hours, minutes, seconds] = timeStr.split(':');
    
    if (includeSeconds) {
      return `${hours}:${minutes}:${seconds}`;
    } else {
      return `${hours}:${minutes}`;
    }
  } catch (error) {
    return timeStr;
  }
}

/**
 * Formats timezone offset to user-friendly format
 */
export function formatTimezone(timezone: string): string {
  try {
    if (timezone === '+00:00') {
      return 'UTC';
    }
    
    const match = timezone.match(/^([+-])(\d{2}):(\d{2})$/);
    if (match) {
      const [, sign, hours, minutes] = match;
      const hoursNum = parseInt(hours);
      const minutesNum = parseInt(minutes);
      
      if (minutesNum === 0) {
        return `UTC${sign}${hoursNum}`;
      } else {
        return `UTC${sign}${hoursNum}:${minutes}`;
      }
    }
    
    return timezone;
  } catch (error) {
    return timezone;
  }
}

/**
 * Formats complete ChartDateTime to user-friendly string
 */
export function formatChartDateTime(
  chartDateTime: ChartDateTime, 
  options: {
    dateFormat?: 'short' | 'medium' | 'long';
    includeSeconds?: boolean;
    includeTimezone?: boolean;
    includeLocation?: boolean;
  } = {}
): string {
  const {
    dateFormat = 'medium',
    includeSeconds = false,
    includeTimezone = true,
    includeLocation = true
  } = options;

  try {
    const parts: string[] = [];
    
    // Add formatted date
    parts.push(formatDate(chartDateTime.date, dateFormat));
    
    // Add formatted time
    parts.push(formatTime(chartDateTime.time, includeSeconds));
    
    // Add timezone if requested
    if (includeTimezone) {
      parts.push(formatTimezone(chartDateTime.timezone));
    }
    
    // Add location if available and requested
    if (includeLocation && chartDateTime.location) {
      parts.push(`(${chartDateTime.location})`);
    }
    
    return parts.join(' ');
  } catch (error) {
    return `${chartDateTime.date} ${chartDateTime.time}`;
  }
}

/**
 * Formats a Date object to chart name format (YYYY-MM-DD-HH-mm)
 */
export function formatDateForFilename(date: Date): string {
  try {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${year}-${month}-${day}-${hours}-${minutes}`;
  } catch (error) {
    return 'unknown-date';
  }
}

/**
 * Formats file size in bytes to human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  
  const units = ['B', 'KB', 'MB', 'GB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`;
}

/**
 * Formats relative time (e.g., "2 hours ago", "Yesterday")
 */
export function formatRelativeTime(date: Date): string {
  try {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMinutes < 1) {
      return 'Just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return formatDate(date.toISOString().split('T')[0], 'short');
    }
  } catch (error) {
    return 'Unknown time';
  }
}

/**
 * Parses and formats duration in a human-readable way
 */
export function formatDuration(startDate: Date, endDate: Date): string {
  try {
    const diffMs = endDate.getTime() - startDate.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return `${diffDays} day${diffDays === 1 ? '' : 's'}`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'}`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'}`;
    } else {
      return 'Less than a minute';
    }
  } catch (error) {
    return 'Unknown duration';
  }
}
